import { useColorRuleConfig } from '../../core/context/ColorRuleContext.js';
import { safeGet } from '../../core/utils/safe-get.js';
import { buildValidator, VALUE_MATCHERS } from '../../core/utils/validators.js';
import {
  BAR_VARIANT,
  MapDatapoints,
  RECT_VARIANT,
  type SeriesToMetadata,
  type XYChartSeriesWithDatapoints,
} from '../types/xy-chart-internals.js';
import { applyColorRuleToBarSeries } from '../utils/coloring/apply-color-rule-to-bar-series.js';
import { applyColorRuleToRectSeries } from '../utils/coloring/apply-color-rule-to-rect-series.js';
import { applyColorRuleToSeriesMetadata } from '../utils/coloring/apply-color-rule-to-series-metadata.js';
import { hasSeriesMatchingDatapoint } from '../utils/coloring/has-series-matching-datapoint.js';

type UseColorRule = (args: {
  seriesWithDatapoints: XYChartSeriesWithDatapoints[];
  seriesToMetadata: SeriesToMetadata;
  datapointMetadataMap: MapDatapoints;
}) => SeriesToMetadata;

export const useColorRule: UseColorRule = ({
  seriesWithDatapoints,
  seriesToMetadata,
  datapointMetadataMap,
}) => {
  const colorRules = useColorRuleConfig();

  if (!colorRules.length) {
    return seriesToMetadata;
  }

  const reversedRules = colorRules.toReversed();
  const isValid = buildValidator(VALUE_MATCHERS);

  seriesWithDatapoints.forEach((series, index) => {
    const seriesMetadata = safeGet(seriesToMetadata, series);

    if (seriesMetadata.colorIsFromSlot) {
      return;
    }

    if (series.type === RECT_VARIANT) {
      const categoricalColorOverrides = applyColorRuleToRectSeries(
        series,
        reversedRules,
        isValid,
        datapointMetadataMap,
      );

      if (categoricalColorOverrides) {
        seriesToMetadata.set(series, {
          ...seriesMetadata,
          categoricalColorOverrides,
        });
      }
    } else if (series.type === BAR_VARIANT) {
      applyColorRuleToBarSeries(
        series,
        reversedRules,
        isValid,
        datapointMetadataMap,
        seriesToMetadata,
        seriesMetadata,
        index,
      );
    } else {
      const validColorRule = reversedRules.find((rule) =>
        hasSeriesMatchingDatapoint(series, rule, isValid),
      );

      if (validColorRule) {
        applyColorRuleToSeriesMetadata(
          seriesToMetadata,
          series,
          seriesMetadata,
          validColorRule,
          index,
        );
      }
    }
  });

  return seriesToMetadata;
};
